import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';

const formatDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${date} at ${time}`;
};

const SocialEventRsvpPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // true/false after submit

  useEffect(() => {
    (async () => {
      try {
        const data = await unifiedApi.getSocialEventByToken(token);
        if (!data) {
          setError('Invalid or expired link.');
        } else {
          setEvent(data.socialEvent);
          setRecipient(data.recipient);
          if (data.socialEvent?.currentUserAttending != null) {
            setSubmitted(data.socialEvent.currentUserAttending);
          }
        }
      } catch (err) {
        setError(err?.message || 'Invalid or expired link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (attending) => {
    setSubmitting(true);
    setError(null);
    try {
      await unifiedApi.submitSocialEventTokenRsvp(token, attending);
      setSubmitted(attending);
    } catch (err) {
      setError(err?.message || 'Failed to submit RSVP.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !event) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, px: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>{event.title}</Typography>
        {recipient && (
          <Typography color="text.secondary" gutterBottom>
            Hi {recipient.firstName}, please let us know if you can make it.
          </Typography>
        )}
        <Stack spacing={1} sx={{ my: 2 }}>
          <Typography><strong>When:</strong> {formatDateTime(event.start)}</Typography>
          {event.location && <Typography><strong>Location:</strong> {event.location}</Typography>}
          {event.description && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{event.description}</Typography>}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {submitted == null ? (
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              disabled={submitting}
              onClick={() => submit(true)}
            >
              I&apos;ll be there
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={submitting}
              onClick={() => submit(false)}
            >
              Can&apos;t make it
            </Button>
          </Stack>
        ) : (
          <>
            <Alert severity="success" sx={{ mt: 2 }}>
              Thanks! You&apos;re marked as <strong>{submitted ? 'attending' : 'not attending'}</strong>.
            </Alert>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={submitting}
                onClick={() => submit(!submitted)}
              >
                Change to {submitted ? 'not attending' : 'attending'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SocialEventRsvpPage;
